import { WaveCloudAPI, WAVE_APP_ID } from "@vida/wave-core";
import { createWaveApiConfig, trackActivity } from "./waveSession";
import { searchWaveDocuments } from "./waveRag";
import { buildContextFromChunks, pollAIJob } from "./waveAiJobHelpers";
import { WAVE_AGENT_MODEL_ID } from "./waveAgentModel";

export const WAVE_ANALYST_SYSTEM_TEXT =
  "Sei @analista, un agente specializzato nell'analisi di documenti allegati a thread Wave. " +
  "Rispondi in italiano usando SOLO le informazioni negli estratti del documento indicato. " +
  "Se la risposta non è nel documento, dillo chiaramente. " +
  "Struttura le risposte in modo chiaro (punti elenco quando utile). " +
  "Cita il nome file quando rilevante. " +
  "Mantieni coerenza con la cronologia della conversazione.";

export const WAVE_ANALYST_TEXT_SYSTEM_TEXT =
  "Sei @analista, un agente specializzato nell'analisi del contenuto di widget testuali in thread Wave. " +
  "Rispondi in italiano usando SOLO le informazioni nel testo del widget indicato. " +
  "Se la risposta non è nel testo, dillo chiaramente. " +
  "Struttura le risposte in modo chiaro (punti elenco quando utile). " +
  "Mantieni coerenza con la cronologia della conversazione.";

export type WaveAnalystMessage = {
  role: "user" | "assistant";
  content: string;
};

export type WaveAnalystTarget =
  | {
      blockId: string;
      sourceKind: "file";
      s3Key: string;
      fileName: string;
    }
  | {
      blockId: string;
      sourceKind: "text";
      label: string;
      textContent: string;
    };

/** Human-readable label for FAB/header (file name or text widget label). */
export function analystTargetLabel(target: WaveAnalystTarget): string {
  return target.sourceKind === "file" ? target.fileName : target.label;
}

function buildTranscript(history: WaveAnalystMessage[]): string {
  if (!history.length) return "(Prima domanda.)";
  return history
    .map((m) => `${m.role === "user" ? "Utente" : "Analista"}: ${m.content}`)
    .join("\n\n");
}

async function submitAnalystJob(
  waveId: string,
  question: string,
  systemText: string,
  userText: string,
  extraParams: Record<string, unknown>,
  activityDetail: string,
): Promise<string> {
  const cfg = createWaveApiConfig();
  const params = {
    bedrock: {
      systemText,
      userText,
      maxTokens: 4096,
    },
    waveId,
    ...extraParams,
  };

  const result = await WaveCloudAPI.submitAIJob(cfg, {
    kind: "BEDROCK_CONVERSE",
    provider: "BEDROCK",
    prompt: question,
    paramsJson: JSON.stringify(params),
    inputAssetKeys: [],
    appId: WAVE_APP_ID,
    modelId: WAVE_AGENT_MODEL_ID,
    feature: "wave.agent.analyst",
  });

  trackActivity("@analista", `${activityDetail} (job ${result.jobId.slice(0, 8)}…)`);
  return pollAIJob(result.jobId);
}

/** Run one @analista turn scoped to a single indexed attachment. */
export async function runAnalystTurn(
  waveId: string,
  s3Key: string,
  fileName: string,
  question: string,
  history: WaveAnalystMessage[] = [],
): Promise<string> {
  const trimmed = question.trim();
  if (!trimmed) throw new Error("Domanda vuota.");

  const chunks = await searchWaveDocuments(waveId, trimmed, 8, s3Key);
  const context = buildContextFromChunks(chunks);
  const transcript = buildTranscript(history);

  return submitAnalystJob(
    waveId,
    trimmed,
    WAVE_ANALYST_SYSTEM_TEXT,
    `DOCUMENTO: ${fileName}\n` +
      `WAVE: ${waveId}\n\n` +
      `CRONOLOGIA:\n${transcript}\n\n` +
      `DOMANDA CORRENTE:\n${trimmed}\n\n` +
      `ESTRATTI DOCUMENTO:\n${context}`,
    { s3Key },
    "Analisi documento",
  );
}

/** Run one @analista turn scoped to a TEXT widget (content passed directly, no RAG). */
export async function runAnalystTextTurn(
  waveId: string,
  label: string,
  textContent: string,
  question: string,
  history: WaveAnalystMessage[] = [],
): Promise<string> {
  const trimmed = question.trim();
  if (!trimmed) throw new Error("Domanda vuota.");

  const transcript = buildTranscript(history);
  const content = textContent.trim() || "(Widget vuoto.)";

  return submitAnalystJob(
    waveId,
    trimmed,
    WAVE_ANALYST_TEXT_SYSTEM_TEXT,
    `WIDGET: ${label}\n` +
      `WAVE: ${waveId}\n\n` +
      `CRONOLOGIA:\n${transcript}\n\n` +
      `DOMANDA CORRENTE:\n${trimmed}\n\n` +
      `CONTENUTO DEL WIDGET:\n${content}`,
    { blockLabel: label },
    "Analisi widget testo",
  );
}

/** Dispatch one @analista turn based on target source kind. */
export async function runAnalystTurnForTarget(
  waveId: string,
  target: WaveAnalystTarget,
  question: string,
  history: WaveAnalystMessage[] = [],
): Promise<string> {
  if (target.sourceKind === "file") {
    return runAnalystTurn(
      waveId,
      target.s3Key,
      target.fileName,
      question,
      history,
    );
  }
  return runAnalystTextTurn(
    waveId,
    target.label,
    target.textContent,
    question,
    history,
  );
}

/** Format saved widget text from a Q&A pair. */
export function formatAnalystWidgetText(question: string, answer: string): string {
  return `**Domanda:** ${question.trim()}\n\n**Risposta:**\n${answer.trim()}`;
}

const SESSION_PREFIX = "wave:analyst:";

export function analystSessionKey(waveId: string, blockId: string): string {
  return `${SESSION_PREFIX}${waveId}#${blockId}`;
}

export function loadAnalystHistory(waveId: string, blockId: string): WaveAnalystMessage[] {
  try {
    const raw = sessionStorage.getItem(analystSessionKey(waveId, blockId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as WaveAnalystMessage[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveAnalystHistory(
  waveId: string,
  blockId: string,
  history: WaveAnalystMessage[],
): void {
  try {
    sessionStorage.setItem(analystSessionKey(waveId, blockId), JSON.stringify(history));
  } catch {
    /* quota exceeded — ignore */
  }
}
